const UserRole = {
  SALES_REP: 'sales_rep',
  CX_PRO: 'cx_pro',
  CUSTOMER: 'customer',
} as const;

// Create a type from the values of the UserRole object
type UserRoleType = (typeof UserRole)[keyof typeof UserRole];

/**
 * Interface for chatroom data from the database, this is shared by the
 * sales chat experience frontend and backend for sales reps, customers, and here.
 */
type DBChatroomData = {
  /** The id for the chatroom */
  id: string;

  /** The name for the chatroom */
  name?: string;

  /** The address for the chatroom */
  address?: string;

  /** The oppty id for the chatroom */
  opptyId?: string;

  /** The firebase user ids in the chatroom (automatically added by the system) */
  firebaseUserIds?: string[];

  /** Sunrun employee contact ids participating in the chatroom */
  sunrunContactIds?: string[];

  /** The last message in the chatroom */
  lastMessage?: DBMessage;

  /** The number of messages in the chatroom */
  messageCount?: number;

  /**
   * Per-contactId unread message counts (maps Sunrun contactId -> unread count).
   * When present for the current user, takes precedence over role-based fields below.
   */
  unreadCountsByContactId?: Record<string, number>;

  /** The number of unread messages for the sales rep */
  numUnreadMessagesSalesRep?: number;

  /** The number of unread messages for the cx pro */
  numUnreadMessagesCxPro?: number;

  /** The number of unread messages for the customer */
  numUnreadMessagesCustomer?: number;
};

/**
 * INTERFACE FOR CHAT MESSAGES STARTS HERE
 */
/** Message structure for a single chat message */
interface DBMessage {
  /** Unique identifier for the message */
  messageId: string;

  /** The sender firebase id of the message */
  senderFirebaseUserId: string;

  /** The role of the sender */
  senderRole: UserRoleType;

  /** The sf contact id that the message belongs to */
  contactId: string;

  /** The unix millisecond timestamp of the message */
  sentAt: number;

  /** Whether the message has been seen by different users */
  seen: Seen[];

  /** User-facing name for messages of type FILE */
  text?: string | undefined;

  /** The media urls for the message */
  mediaUrls?: MediaUrl[] | undefined;
}

/** The media url for the message */
interface MediaUrl {
  /** The url of the media */
  url: string;

  /** The name of the media */
  name: string;

  /** The type of the media */
  type: 'pdf' | 'image';
}

interface Seen {
  // Firebase user id of the viewer
  firebaseUserId: string;

  /** The role of the viewer */
  role: UserRoleType;

  // SF contact id of the viewer
  contactId: string;

  /** The unix millisecond timestamp of when the message was seen */
  seenAt: number;
}

export { UserRole };
export type { UserRoleType, DBChatroomData, DBMessage, MediaUrl, Seen };
